<template>
  <div>
    <div class="d-flex align-items-center mb-3">
      <span v-if="!hasStripeApiKey" v-b-tooltip.hover.html="createSubscriptionTooltip">
        <b-button
          variant="primary"
          class="mr-auto"
          :disabled="!hasStripeApiKey"
          @click="createNewSubscription"
        >
          Create Subscription
        </b-button>
      </span>
      <b-button
        v-else
        variant="primary"
        class="mr-auto"
        :disabled="!hasStripeApiKey"
        @click="createNewSubscription"
      >
        Create Subscription
      </b-button>
      <div class="d-flex align-items-center ml-auto">
        <label for="api-key" class="font-weight-bold mb-0 mr-2">Stripe API Key</label>
        <span
          v-b-tooltip.hover.html="stripeApiKeyTooltip"
          class="d-inline-block mr-2"
          tabindex="0"
          style="cursor:pointer;"
        >
          <i class="uil uil-question-circle" aria-label="Help"></i>
        </span>
        <input
          id="api-key"
          :value="editingApiKey ? apiKey : maskedApiKey"
          type="text"
          class="form-control d-inline-block w-auto mr-2"
          style="max-width:450px"
          :disabled="savingApiKey"
          @input="onApiKeyInput"
          @focus="editingApiKey = true"
          @blur="editingApiKey = false"
        />
        <b-button variant="success" :disabled="savingApiKey || !apiKey" @click="saveApiKey">
          <b-spinner v-if="savingApiKey" small />
          <span v-else>Save</span>
        </b-button>
        <span v-if="apiKeySaved" class="text-success ml-2">Saved!</span>
      </div>
    </div>
    <div class="card bg-white rounded-lg border border-gray-400 mt-3">
      <div class="card-body p-0">
        <div class="overflow-hidden rounded-lg">
          <table class="table table-striped w-full mb-0">
            <thead>
              <tr>
                <th colspan="6" class="p-4">
                  <div class="d-flex align-items-center justify-content-between">
                    <div class="form-inline">
                      <label>Show&nbsp;</label>
                      <b-form-select
                        v-model="queries.per_page"
                        class="ml-1 mr-1"
                        size="sm"
                        :options="[10, 15, 25, 50, 100]"
                        @change="getSubscriptions"
                      ></b-form-select>
                      <span>entries</span>
                    </div>
                    <div class="form-inline ml-auto">
                      <label class="d-inline-flex align-items-center">
                        Search:
                        <b-form-input
                          v-model="queries.search"
                          type="search"
                          placeholder="Search..."
                          class="form-control form-control-sm ml-2"
                          @keyup.enter="getSubscriptions"
                        ></b-form-input>
                      </label>
                      <button class="ml-1 btn btn-dark-blue btn-sm" :disabled="searching" @click="getSubscriptions">
                        <span v-if="searching">
                          <b-spinner small class="mr-1" />
                        </span>
                        <i v-else class="uil uil-search-alt"></i>
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="6" class="text-center">
                  <b-spinner label="Loading..." variant="primary" small></b-spinner>
                </td>
              </tr>
              <tr v-else-if="subscriptions.length === 0">
                <td colspan="6" class="text-center py-5">No subscriptions found.</td>
              </tr>
              <tr v-else>
                <th class="pl-4 px-6 py-2 text-left font-semibold">Name</th>
                <th class="px-6 py-2 text-left font-semibold">Link</th>
                <th class="px-6 py-2 text-left font-semibold">Type</th>
                <th class="px-6 py-2 text-left font-semibold">Amount</th>
                <th class="px-6 py-2 text-left font-semibold">Interval</th>
                <th class="px-6 py-2 text-left font-semibold" width="220">Actions</th>
              </tr>
              <tr v-for="item in subscriptions" :key="item.id">
                <td class="pl-4 px-6 py-2 border-bottom font-weight-bold">{{ item.name }}</td>
                <td class="px-6 py-2">
                  <a :href="fullCheckoutUrl(item.id)" target="_blank" class="ml-2 text-success">
                    {{ fullCheckoutUrl(item.id) }}
                  </a>
                </td>
                <td class="px-6 py-2 border-bottom">
                  <b-badge :variant="item.mode === 'subscription' ? 'success' : 'info'">
                    {{ item.mode === 'subscription' ? 'Recurring' : 'One-Time' }}
                  </b-badge>
                </td>
                <td class="px-6 py-2 border-bottom">${{ Number(item.amount).toFixed(2) }}</td>
                <td class="px-6 py-2 border-bottom">
                  <span v-if="item.mode === 'subscription'">{{ capitalize(item.interval) }}</span>
                  <span v-else>-</span>
                </td>
                <td class="px-6 py-2 border-bottom">
                  <b-button size="sm" variant="outline-primary" class="mr-2" @click="editSubscription(item)">
                    <i class="fa fa-edit"></i> Edit
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="deleteSubscription(item)">
                    <b-spinner v-if="deletingId === item.id" small type="grow" class="mr-1" />
                    <i v-else class="fa fa-trash"></i> Delete
                  </b-button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6" class="pt-4 px-4">
                  <div class="row">
                    <div class="col">
                      <div class="float-left">
                        Total {{ totalSubscriptions }} subscriptions
                      </div>
                      <div class="dataTables_paginate paging_simple_numbers float-right">
                        <ul class="pagination pagination-rounded mb-0">
                          <b-pagination
                            v-model="queries.page"
                            :total-rows="totalSubscriptions"
                            :per-page="queries.per_page"
                            @change="onPageChanged"
                          ></b-pagination>
                        </ul>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
    <b-modal v-model="showModal" title="Subscription Form" size="lg" hide-footer>
      <AgencySubscriptionForm :form-data="selectedSubscription" @close="showModal = false" @created="onCreated" />
    </b-modal>
  </div>
</template>

<script>
import AgencySubscriptionForm from './AgencySubscriptionForm.vue';
export default {
  name: 'AgencySubscriptionIndex',
  components: { AgencySubscriptionForm },
  data() {
    return {
      showModal: false,
      loading: true,
      searching: false,
      selectedSubscription: null,
      queries: {
        page: 1,
        per_page: 10,
        search: ''
      },
      subscriptions: [],
      totalSubscriptions: 0,
      fields: [
        { key: 'name', label: 'Name' },
        { key: 'mode', label: 'Type' },
        { key: 'amount', label: 'Amount' },
        { key: 'interval', label: 'Interval' },
        { key: 'actions', label: 'Actions' }
      ],
      apiKey: '',
      savingApiKey: false,
      apiKeySaved: false,
      editingApiKey: false,
      deletingId: null,
      createSubscriptionTooltip: `<span>You must add a <b>Stripe API key</b> before you can create a subscription.</span>`,
      stripeApiKeyTooltip: `You can get your Stripe API key from <a href='https://dashboard.stripe.com/apikeys' target='_blank' rel='noopener noreferrer'>Stripe Dashboard</a>.`,
    };
  },
  computed: {
    agencyId() {
      return this.$store.getters['auth/user']?.agency?.id;
    },
    user() {
      return this.$store.getters['auth/user'] || {};
    },
    maskedApiKey() {
      if (!this.apiKey) return '';
      if (this.apiKey.length <= 8) return '****';
      return (
        this.apiKey.slice(0, 4) +
        '****' +
        this.apiKey.slice(-4)
      );
    },
    hasStripeApiKey() {
      return !!(this.user.agency && this.user.agency.settings && this.user.agency.settings.stripe_api_key);
    },
  },
  created() {
    this.apiKey = this.user.agency?.settings?.stripe_api_key || '';
    this.getSubscriptions();
  },
  methods: {
    getSubscriptions() {
      this.loading = true;
      this.searching = true;
      const params = {
        agencyId: this.agencyId,
        page: this.queries.page,
        per_page: this.queries.per_page,
        search: this.queries.search
      };
      this.$store.dispatch('agency/fetchSubscriptions', params)
        .then(res => {
          this.subscriptions = res.data || [];
          this.totalSubscriptions = res.total || 0;
        })
        .finally(() => {
          this.loading = false;
          this.searching = false;
        });
    },
    onPageChanged(page) {
      this.queries.page = page;
      this.getSubscriptions();
    },
    createNewSubscription(){
      this.selectedSubscription = null;
      // Now navigates to new page
      this.$router.push({ name: 'AgencySubscriptionCreate' });
    },
    onApiKeyInput(e) {
      this.apiKey = e.target.value;
    },
    saveApiKey() {
      if (!this.apiKey) return;
      this.savingApiKey = true;
      this.apiKeySaved = false;
      const params = {
        stripe_api_key: this.apiKey,
        setting_form: 'stripe_api_key'
      };
      this.$store.dispatch('agency/editSettings', params)
        .then(() => {
          this.apiKeySaved = true;
          setTimeout(() => {
            this.apiKeySaved = false;
          }, 5000);
        })
        .catch(() => {
          this.apiKeySaved = false;
        })
        .finally(() => {
          this.savingApiKey = false;
        });
    },
    capitalize(value) {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    editSubscription(item) {
      // Now navigates to new page
      this.$router.push({ name: 'AgencySubscriptionEdit', params: { id: item.id } });
    },
    deleteSubscription(item) {
      this.$bvModal && this.$bvModal.msgBoxConfirm('Are you sure you want to delete this subscription?', {
        title: 'Confirm Delete',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        centered: true
      }).then(confirmed => {
        if (confirmed) {
          this.deletingId = item.id;
          this.$store.dispatch('agency/deleteSubscription', item.id)
            .then(() => {
              this.getSubscriptions();
            })
            .finally(() => {
              this.deletingId = null;
            });
        }
      });
    },
    onCreated() {
      this.showModal = false;
      this.selectedSubscription = null;
      this.getSubscriptions();
    },
    fullCheckoutUrl(id) {
      return (typeof window !== 'undefined' ? window.location.origin : '') + this.$router.resolve({ name: 'agency.subscriptions.checkout', params: { id } }).href;
    }
  }
};
</script>
